import { useState } from "react";
import { FileDown, Images } from "lucide-react";
import { useProductAttachments, fileUrl } from "../lib/products";
import { Lightbox } from "./Lightbox";

const formatSize = (bytes) =>
    bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const AttachmentsBlock = ({ productKey, dark = false }) => {
    const attachments = useProductAttachments(productKey);
    const [preview, setPreview] = useState(null);
    const pdfs = attachments.filter((a) => a.kind === "pdf");
    const images = attachments.filter((a) => a.kind === "image");
    if (!attachments.length) return null;

    const text = dark ? "text-white" : "text-[#0B1F3A]";
    const muted = dark ? "text-white/60" : "text-[#4F5D70]";

    return (
        <div className="mt-6 space-y-5" data-testid={`attachments-${productKey}`}>
            {pdfs.length > 0 && (
                <div>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${muted}`}>
                        Depliant e documenti
                    </div>
                    <ul className="mt-2.5 space-y-2">
                        {pdfs.map((a) => (
                            <li key={a.id}>
                                <a
                                    href={fileUrl(a, true)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`attachment-pdf-${a.id}`}
                                    className={`group flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${
                                        dark
                                            ? "border-white/15 bg-white/5 hover:border-[#E30613]/60"
                                            : "border-slate-200 bg-slate-50 hover:border-[#E30613]/50 hover:bg-white"
                                    }`}
                                >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#E30613] text-white">
                                        <FileDown className="h-4 w-4" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className={`block truncate text-sm font-bold ${text}`}>{a.label}</span>
                                        <span className={`block text-xs ${muted}`}>PDF · {formatSize(a.size)}</span>
                                    </span>
                                    <span className="text-xs font-bold text-[#E30613]">Scarica</span>
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {images.length > 0 && (
                <div>
                    <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] ${muted}`}>
                        <Images className="h-3.5 w-3.5" /> Altre immagini
                    </div>
                    <div className="mt-2.5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {images.map((a, i) => (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => setPreview(i)}
                                data-testid={`attachment-image-${a.id}`}
                                className="group flex aspect-[4/3] items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-1.5 transition-colors hover:border-[#E30613]/50"
                                aria-label={a.label}
                            >
                                <img
                                    src={fileUrl(a)}
                                    alt={a.label}
                                    loading="lazy"
                                    className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                                />
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {preview !== null && (
                <Lightbox items={images.map((a) => ({ src: fileUrl(a), label: a.label }))} index={preview} onClose={() => setPreview(null)} onChange={setPreview} />
            )}
        </div>
    );
};
